<template>
  <router-view />
  <button
    class="global-home-btn"
    type="button"
    :style="globalHomeStyle"
    @click="handleGlobalHomeClick"
    @pointerdown="handleGlobalHomePointerDown"
    aria-label="返回首页"
  >
    <img src="@/assets/首页.svg" alt="首页" class="global-home-icon" />
  </button>
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";

export default {
  name: "App",
  setup() {
    const router = useRouter();

    const BTN_SIZE = 52;
    const STORAGE_KEY = "global_home_btn_pos_v1";

    const isDragging = ref(false);
    const dragMoved = ref(false);
    const startPointer = ref({ x: 0, y: 0 });
    const startPos = ref({ x: 0, y: 0 });

    const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

    const getSafeTop = () => 0;

    const getDefaultPos = () => {
      const vw = window.innerWidth || 375;
      const vh = window.innerHeight || 667;

      const x = vw - 18 - BTN_SIZE;
      const y = vh - 90 - BTN_SIZE;
      return {
        x: clamp(x, 8, vw - BTN_SIZE - 8),
        y: clamp(y, getSafeTop() + 8, vh - BTN_SIZE - 8),
      };
    };

    const pos = ref(getDefaultPos());

    const loadPos = () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        const x = Number(parsed?.x);
        const y = Number(parsed?.y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        pos.value = { x, y };
      } catch (e) {
        // ignore
      }
    };

    const savePos = () => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pos.value));
      } catch (e) {
        // ignore
      }
    };

    const ensureInViewport = () => {
      const vw = window.innerWidth || 375;
      const vh = window.innerHeight || 667;

      const x = clamp(pos.value.x, 8, vw - BTN_SIZE - 8);
      const y = clamp(pos.value.y, getSafeTop() + 8, vh - BTN_SIZE - 8);
      pos.value = { x, y };
    };

    const globalHomeStyle = computed(() => ({
      left: `${pos.value.x}px`,
      top: `${pos.value.y}px`,
    }));

    const goHome = () => {
      try {
        sessionStorage.removeItem("pls-receive-scan-cache");
      } catch (e) {
        // ignore
      }
      router.replace({ path: "/home" });
    };

    const handleGlobalHomePointerDown = (e) => {
      if (e?.button !== undefined && e.button !== 0) return;
      isDragging.value = true;
      dragMoved.value = false;
      startPointer.value = { x: e.clientX, y: e.clientY };
      startPos.value = { x: pos.value.x, y: pos.value.y };
      try {
        e.currentTarget?.setPointerCapture?.(e.pointerId);
      } catch (err) {
        // ignore
      }
      e.preventDefault();
    };

    const handlePointerMove = (e) => {
      if (!isDragging.value) return;
      const dx = e.clientX - startPointer.value.x;
      const dy = e.clientY - startPointer.value.y;
      if (Math.abs(dx) + Math.abs(dy) > 6) dragMoved.value = true;

      const next = { x: startPos.value.x + dx, y: startPos.value.y + dy };
      pos.value = next;
      ensureInViewport();
      e.preventDefault();
    };

    const handlePointerUp = () => {
      if (!isDragging.value) return;
      isDragging.value = false;
      ensureInViewport();
      savePos();
    };

    const handleGlobalHomeClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (dragMoved.value) {
        dragMoved.value = false;
        return;
      }
      goHome();
    };

    const handleResize = () => {
      ensureInViewport();
      savePos();
    };

    // 初始化主题和语言
    const initThemeAndLanguage = () => {
      // 初始化主题
      const savedTheme = localStorage.getItem("systemTheme") || "light";
      document.documentElement.setAttribute("data-theme", savedTheme);
      if (savedTheme === "auto") {
        const prefersDark = window.matchMedia(
          "(prefers-color-scheme: dark)",
        ).matches;
        document.documentElement.setAttribute(
          "data-theme",
          prefersDark ? "dark" : "light",
        );
      }

      // 初始化语言
      const savedLanguage = localStorage.getItem("systemLanguage") || "zh-CN";
      document.documentElement.setAttribute("lang", savedLanguage);
    };

    onMounted(() => {
      loadPos();
      ensureInViewport();
      initThemeAndLanguage();
      window.addEventListener("pointermove", handlePointerMove, {
        passive: false,
      });
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
      window.addEventListener("resize", handleResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
      window.removeEventListener("resize", handleResize);
    });

    return {
      globalHomeStyle,
      handleGlobalHomePointerDown,
      handleGlobalHomeClick,
      goHome,
    };
  },
  mounted() {
    // 防止通过键盘快捷键缩放
    window.addEventListener("keydown", this.preventZoomKeys);
    // 防止通过触摸手势缩放
    document.addEventListener("touchstart", this.preventPinchZoom, {
      passive: false,
    });
    // 防止双击缩放
    document.addEventListener("dblclick", this.preventDoubleClickZoom, {
      passive: false,
    });
    // 防止触摸move事件中的缩放
    document.addEventListener("touchmove", this.preventZoomMove, {
      passive: false,
    });
  },
  beforeUnmount() {
    // 清理事件监听器
    window.removeEventListener("keydown", this.preventZoomKeys);
    document.removeEventListener("touchstart", this.preventPinchZoom);
    document.removeEventListener("dblclick", this.preventDoubleClickZoom);
    document.removeEventListener("touchmove", this.preventZoomMove);
  },
  methods: {
    // 阻止缩放快捷键
    preventZoomKeys(event) {
      // Ctrl/Cmd + + 或 Ctrl/Cmd + -
      if (
        (event.ctrlKey || event.metaKey) &&
        (event.key === "+" || event.key === "-")
      ) {
        event.preventDefault();
      }
      // Ctrl/Cmd + 滚轮 也会触发此事件
      if ((event.ctrlKey || event.metaKey) && event.key === "0") {
        event.preventDefault();
      }
    },
    // 阻止双指缩放手势
    preventPinchZoom(event) {
      // 检测是否为多指触摸
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    },
    // 阻止双击缩放
    preventDoubleClickZoom(event) {
      event.preventDefault();
    },
    // 阻止触摸移动中的缩放
    preventZoomMove(event) {
      // 检测是否为多指触摸移动
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    },
  },
};
</script>

<style>
/* 主题变量定义 */
:root {
  /* 浅色主题 */
  --background-color: #f8f9fa;
  --card-background: #ffffff;
  --text-primary: #2d3748;
  --text-secondary: #718096;
  --text-light: #a0aec0;
  --border-color: #f0f0f0;
  --primary-color: #23b1c0;
  --primary-hover: #1a95a3;
  --success-color: #48bb78;
  --warning-color: #ed8936;
  --error-color: #e53e3e;
  --shadow-light: rgba(0, 0, 0, 0.05);
  --shadow-medium: rgba(0, 0, 0, 0.1);
  --header-background: #23b1c0;
  --header-text: #ffffff;
}

/* 深色主题 */
[data-theme="dark"] {
  --background-color: #1a202c;
  --card-background: #2d3748;
  --text-primary: #f7fafc;
  --text-secondary: #cbd5e0;
  --text-light: #718096;
  --border-color: #4a5568;
  --primary-color: #38b2ac;
  --primary-hover: #319795;
  --success-color: #68d391;
  --warning-color: #ed8936;
  --error-color: #f56565;
  --shadow-light: rgba(0, 0, 0, 0.2);
  --shadow-medium: rgba(0, 0, 0, 0.3);
  --header-background: #1a202c;
  --header-text: #f7fafc;
}

/* 基础样式 */
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: var(--text-primary);
  margin-top: 0;
  background-color: var(--background-color);
}

/* 防止文本缩放 */
* {
  -webkit-text-size-adjust: none;
  -moz-text-size-adjust: none;
  -ms-text-size-adjust: none;
  text-size-adjust: none;
  touch-action: manipulation;
}

/* 防止页面缩放 */
html {
  touch-action: pan-x pan-y;
  -webkit-overflow-scrolling: touch;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* 防止双击缩放和其他触摸操作 */
body {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  background-color: var(--background-color);
  color: var(--text-primary);
}

/* 全局页面内容区基础样式 */
.page,
.content {
  padding-bottom: env(safe-area-inset-bottom);
  background-color: var(--background-color);
}

.global-home-btn {
  position: fixed;
  left: 0;
  top: 0;
  width: 52px;
  height: 52px;
  border-radius: 26px;
  border: none;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  touch-action: none;
}

[data-theme="dark"] .global-home-btn {
  background: rgba(45, 55, 72, 0.95);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.3);
}

.global-home-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

/* 额外的缩放防护 */
*:not(input):not(textarea):not(select) {
  touch-action: manipulation;
}
</style>
